import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Shield, ShieldCheck, Clock, AlertTriangle, CheckCircle2,
  Truck, Package, DollarSign, Loader2, AlertCircle
} from 'lucide-react';
import api from '@/utils/api';

/**
 * Escrow Status Badge - Shows current escrow state
 */
export const EscrowStatusBadge = ({ status }) => {
  const configs = {
    holding: {
      label: 'Funds Protected',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: Shield
    },
    released: {
      label: 'Funds Released',
      color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      icon: CheckCircle2
    },
    refunded: {
      label: 'Refunded',
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: DollarSign
    },
    disputed: {
      label: 'Under Dispute',
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: AlertTriangle
    }
  };
  
  const config = configs[status] || configs.holding;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={config.color}>
      <Icon className="h-3 w-3 mr-1" />
      {config.label}
    </Badge>
  );
};

/**
 * Escrow Card for Buyers - Shows protection status and actions
 */
export const BuyerEscrowCard = ({ purchase, onUpdate }) => {
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDescription, setDisputeDescription] = useState('');
  const [loading, setLoading] = useState(false);
  
  const confirmDelivery = async () => {
    if (!confirm('Are you sure you received the item in the expected condition? This will release funds to the seller.')) {
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/purchases/${purchase.purchase_id}/confirm-delivery`);
      toast.success('Delivery confirmed! Funds released to seller.');
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setLoading(false);
    }
  };
  
  const openDispute = async () => {
    if (!disputeReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/purchases/${purchase.purchase_id}/open-dispute`, {
        reason: disputeReason,
        description: disputeDescription
      });
      toast.success('Dispute opened. We will review and contact both parties.');
      setShowDisputeDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open dispute');
    } finally {
      setLoading(false);
    }
  };
  
  if (purchase.escrow_status !== 'holding') {
    return (
      <Card className="p-4 bg-slate-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
            <span className="text-sm font-medium">Transaction Complete</span>
          </div>
          <EscrowStatusBadge status={purchase.escrow_status} />
        </div>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-blue-800">Buyer Protection Active</h4>
              <EscrowStatusBadge status="holding" />
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Your payment is held securely until you confirm receipt.
            </p>
            
            {purchase.shipping_tracking ? (
              <div className="mt-3 p-2 bg-white rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">Tracking:</span>
                  <span className="text-slate-600">{purchase.shipping_tracking}</span>
                </div>
              </div>
            ) : (
              <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-sm text-amber-700">
                  <Clock className="h-4 w-4" />
                  <span>Waiting for seller to ship</span>
                </div>
              </div>
            )}
            
            <div className="flex gap-2 mt-3">
              <Button 
                size="sm"
                onClick={confirmDelivery}
                disabled={loading}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Confirm Receipt
              </Button>
              <Button 
                size="sm"
                variant="outline"
                onClick={() => setShowDisputeDialog(true)}
                disabled={loading}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Open Dispute
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Dispute Dialog */}
      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Open a Dispute
            </DialogTitle>
            <DialogDescription>
              Please provide details about the issue with your order.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Reason for Dispute</Label>
              <Select value={disputeReason} onValueChange={setDisputeReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_received">Item not received</SelectItem>
                  <SelectItem value="not_as_described">Item not as described</SelectItem>
                  <SelectItem value="damaged">Item damaged</SelectItem>
                  <SelectItem value="counterfeit">Item is counterfeit</SelectItem>
                  <SelectItem value="other">Other issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={disputeDescription}
                onChange={(e) => setDisputeDescription(e.target.value)}
                placeholder="Please describe the issue in detail..."
                className="mt-1"
                rows={4}
              />
            </div>
            
            <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-700">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              Opening a dispute will hold funds until the issue is resolved. Please try to resolve issues directly with the seller first.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisputeDialog(false)}>Cancel</Button>
            <Button 
              onClick={openDispute}
              disabled={loading || !disputeReason}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Escrow Card for Sellers - Shows status and add tracking
 */
export const SellerEscrowCard = ({ purchase, onUpdate }) => {
  const [showTrackingDialog, setShowTrackingDialog] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('USPS');
  const [loading, setLoading] = useState(false);
  
  const addTracking = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/purchases/${purchase.purchase_id}/add-tracking`, {
        tracking_number: trackingNumber,
        carrier: carrier
      });
      toast.success('Tracking added successfully!');
      setShowTrackingDialog(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add tracking');
    } finally {
      setLoading(false);
    }
  };
  
  if (purchase.escrow_status === 'released') {
    return (
      <Card className="p-4 bg-emerald-50 border-emerald-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
            <span className="text-sm font-medium text-emerald-800">Payment Released</span>
          </div>
          <span className="font-semibold text-emerald-700">${purchase.seller_payout?.toFixed(2)}</span>
        </div>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Clock className="h-6 w-6 text-amber-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-amber-800">Payment Pending</h4>
              <span className="font-semibold text-amber-700">${purchase.seller_payout?.toFixed(2)}</span>
            </div>
            <p className="text-sm text-amber-700 mt-1">
              {purchase.seller_shipped 
                ? 'Waiting for buyer to confirm receipt or auto-release after 14 days.'
                : 'Ship the item and add tracking to start the release countdown.'}
            </p>
            
            {purchase.shipping_tracking ? (
              <div className="mt-3 p-2 bg-white rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-sm">
                  <Truck className="h-4 w-4 text-emerald-500" />
                  <span className="font-medium">Shipped!</span>
                  <span className="text-slate-600">{purchase.shipping_tracking}</span>
                </div>
                {purchase.escrow_release_date && (
                  <p className="text-xs text-slate-500 mt-1">
                    Funds auto-release: {new Date(purchase.escrow_release_date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <Button 
                size="sm"
                onClick={() => setShowTrackingDialog(true)}
                className="mt-3 bg-amber-500 hover:bg-amber-600"
              >
                <Truck className="h-4 w-4 mr-2" />
                Add Shipping Tracking
              </Button>
            )}
            
            {purchase.dispute_status === 'open' && (
              <div className="mt-3 p-2 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">Dispute Open</span>
                </div>
                <p className="text-xs text-red-600 mt-1">
                  A dispute has been opened. Please check your email for details.
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Add Tracking Dialog */}
      <Dialog open={showTrackingDialog} onOpenChange={setShowTrackingDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-amber-500" />
              Add Shipping Tracking
            </DialogTitle>
            <DialogDescription>
              Enter your tracking information. The buyer will be notified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tracking Number</Label>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter tracking number"
                className="mt-1"
              />
            </div>
            
            <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700">
              <Shield className="h-4 w-4 inline mr-1" />
              Funds will be automatically released 14 days after shipping if the buyer doesn't confirm earlier.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrackingDialog(false)}>Cancel</Button>
            <Button 
              onClick={addTracking}
              disabled={loading || !trackingNumber.trim()}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
              Add Tracking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default { EscrowStatusBadge, BuyerEscrowCard, SellerEscrowCard };
